import AbstractID from './AbstractID';
import site from './site';
import type {
	User,
	AuditLog,
	Configuration,
	PaginatedResponse,
	StatusSummary,
	ServiceDetail,
	PublicIncident,
	Probe,
	ProbeMetric,
	MetricSeries,
	ServiceGroup,
	AdminService,
	ServiceMetric,
	ServiceDiagnostics,
	Incident,
	AlertChannel
} from './AppTypes';

const baseURL = site.backend.baseURL;
const api = site.backend.api;

function qs(params: Record<string, string | number | boolean | undefined>): string {
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== '') {
			search.set(key, String(value));
		}
	}
	const s = search.toString();
	return s ? `?${s}` : '';
}

async function json<T>(response: Response, message: string): Promise<T> {
	if (!response.ok) throw new Error(message);
	return response.json();
}

// --- Public (unauthenticated) -----------------------------------------------

export async function getConfiguration(): Promise<Configuration> {
	return json(await fetch(`${baseURL}${api.configuration}`), 'Failed to fetch configuration');
}

export async function getStatusSummary(): Promise<StatusSummary> {
	return json(await fetch(`${baseURL}${api.statusSummary}`), 'Failed to fetch status');
}

export async function getServiceDetail(slug: string): Promise<ServiceDetail> {
	return json(await fetch(`${baseURL}${api.statusService}/${encodeURIComponent(slug)}`), 'Failed to fetch service');
}

export async function getStatusIncidents(params: { limit?: number; offset?: number } = {}): Promise<PaginatedResponse<PublicIncident>> {
	return json(await fetch(`${baseURL}${api.statusIncidents}${qs(params)}`), 'Failed to fetch incidents');
}

// --- Users / audit (admin) --------------------------------------------------

export async function getCurrentUser(): Promise<User> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.userMe}`), 'Failed to fetch current user');
}

export async function listUsers(params: { limit?: number; offset?: number; search?: string } = {}): Promise<PaginatedResponse<User>> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.userList}${qs(params)}`), 'Failed to fetch users');
}

export async function getUser(id: number): Promise<User> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.user}/${id}`), 'Failed to fetch user');
}

export async function updateUser(id: number, data: { isAdmin?: boolean }): Promise<User> {
	return json(
		await AbstractID.authenticatedPut(`${baseURL}${api.user}/${id}`, 'application/json', JSON.stringify(data)),
		'Failed to update user'
	);
}

export async function listAuditLogs(params: { limit?: number; offset?: number; action?: string; userId?: number } = {}): Promise<PaginatedResponse<AuditLog>> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.auditList}${qs(params)}`), 'Failed to fetch audit logs');
}

// --- Probes (admin) ---------------------------------------------------------

export async function listProbes(params: { limit?: number; offset?: number; search?: string } = {}): Promise<PaginatedResponse<Probe>> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.probeList}${qs(params)}`), 'Failed to fetch probes');
}

export async function getProbe(id: number): Promise<Probe> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.probe}/${id}`), 'Failed to fetch probe');
}

export async function createProbe(data: Partial<Probe> & { secretKey?: string }): Promise<Probe> {
	return json(await AbstractID.authenticatedPost(`${baseURL}${api.probe}`, 'application/json', JSON.stringify(data)), 'Failed to create probe');
}

export async function updateProbe(id: number, data: Partial<Probe> & { secretKey?: string }): Promise<Probe> {
	return json(await AbstractID.authenticatedPut(`${baseURL}${api.probe}/${id}`, 'application/json', JSON.stringify(data)), 'Failed to update probe');
}

export async function deleteProbe(id: number): Promise<void> {
	await AbstractID.authenticatedDelete(`${baseURL}${api.probe}/${id}`);
}

export async function setProbeMetrics(id: number, metrics: ProbeMetric[]): Promise<ProbeMetric[]> {
	return json(await AbstractID.authenticatedPut(`${baseURL}${api.probe}/${id}/metrics`, 'application/json', JSON.stringify({ metrics })), 'Failed to save metrics');
}

export async function testProbe(
	id: number,
	metrics?: ProbeMetric[]
): Promise<{ status: string; rawStats: unknown; error: string | null; responseTimeMs: number; metrics: Record<string, number | string> }> {
	return json(
		await AbstractID.authenticatedPost(`${baseURL}${api.probe}/${id}/test`, 'application/json', JSON.stringify({ metrics })),
		'Failed to test probe'
	);
}

export async function getProbeMetricSeries(
	id: number,
	params: { from?: string; to?: string; keys?: string[] } = {}
): Promise<{ series: MetricSeries[] }> {
	const query = qs({ from: params.from, to: params.to, keys: params.keys && params.keys.length > 0 ? params.keys.join(',') : undefined });
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.probe}/${id}/series${query}`), 'Failed to fetch metric series');
}

// --- Services (admin) -------------------------------------------------------

export async function listServices(): Promise<AdminService[]> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.serviceList}`), 'Failed to fetch services');
}

export async function getService(id: number): Promise<AdminService> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.service}/${id}`), 'Failed to fetch service');
}

export async function createService(data: Partial<AdminService>): Promise<AdminService> {
	return json(await AbstractID.authenticatedPost(`${baseURL}${api.service}`, 'application/json', JSON.stringify(data)), 'Failed to create service');
}

export async function updateService(id: number, data: Partial<AdminService>): Promise<AdminService> {
	return json(await AbstractID.authenticatedPut(`${baseURL}${api.service}/${id}`, 'application/json', JSON.stringify(data)), 'Failed to update service');
}

export async function deleteService(id: number): Promise<void> {
	await AbstractID.authenticatedDelete(`${baseURL}${api.service}/${id}`);
}

export async function setServiceProbes(id: number, probeIds: number[]): Promise<void> {
	await AbstractID.authenticatedPut(`${baseURL}${api.service}/${id}/probes`, 'application/json', JSON.stringify({ probeIds }));
}

export async function reorderServices(ids: number[]): Promise<void> {
	await AbstractID.authenticatedPut(`${baseURL}${api.service}/reorder`, 'application/json', JSON.stringify({ ids }));
}

export async function reorderServiceGroups(ids: number[]): Promise<void> {
	await AbstractID.authenticatedPut(`${baseURL}${api.serviceGroups}/reorder`, 'application/json', JSON.stringify({ ids }));
}

export async function getServiceMetrics(id: number): Promise<ServiceMetric[]> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.service}/${id}/metrics`), 'Failed to fetch service metrics');
}

export async function setServiceMetrics(id: number, metrics: ServiceMetric[]): Promise<ServiceMetric[]> {
	return json(
		await AbstractID.authenticatedPut(`${baseURL}${api.service}/${id}/metrics`, 'application/json', JSON.stringify({ metrics })),
		'Failed to save service metrics'
	);
}

export async function getServiceDiagnostics(id: number): Promise<ServiceDiagnostics> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.service}/${id}/diagnostics`), 'Failed to fetch service diagnostics');
}

export async function listServiceGroups(): Promise<ServiceGroup[]> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.serviceGroups}`), 'Failed to fetch groups');
}

export async function createServiceGroup(data: Partial<ServiceGroup>): Promise<ServiceGroup> {
	return json(await AbstractID.authenticatedPost(`${baseURL}${api.serviceGroups}`, 'application/json', JSON.stringify(data)), 'Failed to create group');
}

export async function updateServiceGroup(id: number, data: Partial<ServiceGroup>): Promise<ServiceGroup> {
	return json(await AbstractID.authenticatedPut(`${baseURL}${api.serviceGroups}/${id}`, 'application/json', JSON.stringify(data)), 'Failed to update group');
}

export async function deleteServiceGroup(id: number): Promise<void> {
	await AbstractID.authenticatedDelete(`${baseURL}${api.serviceGroups}/${id}`);
}

// --- Incidents (admin) ------------------------------------------------------

export async function listIncidents(params: { limit?: number; offset?: number; type?: string; activeOnly?: boolean } = {}): Promise<PaginatedResponse<Incident>> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.incidentList}${qs(params)}`), 'Failed to fetch incidents');
}

export async function getIncident(id: number): Promise<Incident> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.incident}/${id}`), 'Failed to fetch incident');
}

export async function createIncident(data: Record<string, unknown>): Promise<Incident> {
	return json(await AbstractID.authenticatedPost(`${baseURL}${api.incident}`, 'application/json', JSON.stringify(data)), 'Failed to create incident');
}

export async function addIncidentUpdate(id: number, data: { status: string; body: string }): Promise<void> {
	await AbstractID.authenticatedPost(`${baseURL}${api.incident}/${id}/update`, 'application/json', JSON.stringify(data));
}

export async function deleteIncident(id: number): Promise<void> {
	await AbstractID.authenticatedDelete(`${baseURL}${api.incident}/${id}`);
}

export async function setIncidentServices(id: number, serviceIds: number[]): Promise<void> {
	await AbstractID.authenticatedPut(`${baseURL}${api.incident}/${id}/services`, 'application/json', JSON.stringify({ serviceIds }));
}

// --- Alert channels (admin) -------------------------------------------------

export async function listAlertChannels(): Promise<AlertChannel[]> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.alertChannels}`), 'Failed to fetch channels');
}

export async function createAlertChannel(data: Partial<AlertChannel>): Promise<AlertChannel> {
	return json(await AbstractID.authenticatedPost(`${baseURL}${api.alertChannels}`, 'application/json', JSON.stringify(data)), 'Failed to create channel');
}

export async function updateAlertChannel(id: number, data: Partial<AlertChannel>): Promise<AlertChannel> {
	return json(await AbstractID.authenticatedPut(`${baseURL}${api.alertChannels}/${id}`, 'application/json', JSON.stringify(data)), 'Failed to update channel');
}

export async function deleteAlertChannel(id: number): Promise<void> {
	await AbstractID.authenticatedDelete(`${baseURL}${api.alertChannels}/${id}`);
}

export async function testAlertChannel(id: number): Promise<{ success: boolean }> {
	return json(await AbstractID.authenticatedPost(`${baseURL}${api.alertChannels}/${id}/test`, undefined, ''), 'Failed to test channel');
}

export interface ImportSummary {
	groups: { created: number; updated: number };
	probes: { created: number; updated: number };
	services: { created: number; updated: number };
}

export async function exportConfig(): Promise<unknown> {
	return json(await AbstractID.authenticatedFetch(`${baseURL}${api.configExport}`), 'Failed to export configuration');
}

export async function importConfig(data: unknown): Promise<ImportSummary> {
	return json(
		await AbstractID.authenticatedPost(`${baseURL}${api.configImport}`, 'application/json', JSON.stringify(data)),
		'Failed to import configuration'
	);
}
